import { nip19 } from 'nostr-tools';
import { useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Settings, Rows3, Grid2x2, Columns3, Star, Grid2x2Plus, SquareSlash } from 'lucide-react';
import { Confetti } from '@/components/Confetti';
import NotFound from './NotFound';
import { useAuthor } from '@/hooks/useAuthor';
import { useUserColorMoments, transformToColorMoment } from '@/hooks/useColorMoments';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { ColorMomentCard } from '@/components/ColorMomentCard';
import { ColorPaletteDisplay } from '@/components/ColorPaletteDisplay';
import { CaptureModeSelector } from '@/components/CaptureModeSelector';
import { EmojiPicker } from '@/components/EmojiPicker';
import { WavyTransition } from '@/components/WavyTransition';
import { CurtainTransition } from '@/components/CurtainTransition';
import { TeaTimeOverlay } from '@/components/TeaTimeOverlay';
import { PaletteConstellation } from '@/components/PaletteConstellation';
import { Skeleton } from '@/components/ui/skeleton';
import { genUserName } from '@/lib/genUserName';
import { getEmojiToast } from '@/lib/emojiToasts';
import { safeImageUrl } from '@/lib/utils';
import { useTransition } from '@/hooks/useTransition';
import type { LayoutMode } from '@/hooks/useColorMoments';

const LAYOUT_ICONS: Record<LayoutMode, typeof Rows3> = {
  horizontal: Rows3,
  grid: Grid2x2,
  vertical: Columns3,
  star: Star,
  checkerboard: Grid2x2Plus,
  diagonalStripes: SquareSlash,
};

function ProfileView({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const { data: moments, isLoading } = useUserColorMoments(pubkey);
  const { user } = useCurrentUser();
  const { mutate: createEvent } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { navigateWithTransition } = useTransition();
  const backButtonRef = useRef<HTMLAnchorElement>(null);
  
  // Remix state
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedLayout, setSelectedLayout] = useState<LayoutMode>('horizontal');
  const [selectedName, setSelectedName] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState('');
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiColors, setConfettiColors] = useState<string[]>([]);
  const [showCurtainTransition, setShowCurtainTransition] = useState(false);
  const [pendingColors, setPendingColors] = useState<string[]>([]);
  
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);
  const about = metadata?.about;
  const picture = metadata?.picture;
  
  // Check if this is the current user's profile
  const isOwnProfile = user?.pubkey === pubkey;

  const handleBack = () => {
    const rect = backButtonRef.current?.getBoundingClientRect();
    navigateWithTransition('/', rect);
  };

  const handleRemix = (colors: string[], rect: DOMRect) => {
    setSelectedColors(colors);
    setButtonRect(rect);
    setIsTransitioning(true);
    setShowColorPicker(true);
  };

  const handleTransitionComplete = useCallback(() => {
    // Transition complete
  }, []);

  const handleCancelCapture = () => {
    setIsTransitioning(false);
    setShowColorPicker(false);
  };

  const handleColorsSelected = (colors: string[]) => {
    // Store colors and trigger curtain transition
    setPendingColors(colors);
    setShowCurtainTransition(true);
  };

  const handleCurtainMidpointProfile = () => {
    // At midpoint (curtains fully closed), switch screens
    setSelectedColors(pendingColors);
    setIsTransitioning(false);
    setShowColorPicker(false);
    setShowEmojiPicker(true);
  };

  const handleCurtainCompleteProfile = () => {
    setShowCurtainTransition(false);
    setPendingColors([]);
  };

  const handleEmojiSelected = (emoji: string) => {
    setSelectedEmoji(emoji);
  };

  const handleClearEmoji = () => {
    setSelectedEmoji('');
  };

  const handleSubmit = () => {
    if (!user) return;

    // Capture current values before clearing state
    const colors = [...selectedColors];
    const layout = selectedLayout;
    const name = selectedName.trim();
    const emoji = selectedEmoji;
    
    // Close the modal and trigger confetti
    setShowEmojiPicker(false);
    setConfettiColors(colors.length > 0 ? colors : ['#f43f5e', '#f59e0b', '#10b981', '#3b82f6']);
    setShowConfetti(true);
    setSelectedColors([]);
    setSelectedLayout('horizontal');
    setSelectedName('');
    setSelectedEmoji('');

    const tags: string[][] = [
      ...colors.map(color => ['c', color]),
      ['layout', layout],
      ['alt', `Color moment: ${colors.join(', ')}`],
    ];
    if (name) {
      tags.push(['name', name]);
    }

    createEvent({
      kind: 3367,
      content: emoji,
      tags,
    }, {
      onSuccess: () => {
        if (emoji) {
          const toastMessage = getEmojiToast(emoji, 'vibe');
          toast({ title: `${emoji} ${toastMessage}` });
        } else {
          toast({ title: '✨ Shared!' });
        }
        queryClient.invalidateQueries({ queryKey: ['color-moments-infinite'] });
        queryClient.invalidateQueries({ queryKey: ['following-moments-infinite'] });
        queryClient.invalidateQueries({ queryKey: ['user-color-moments', pubkey] });
      },
    });
  };

  /**
   * Get available layouts based on color count.
   * Grid is only available for 4 or 6 colors.
   * Star is not available for 4 colors (looks like a square/grid).
   * Checkerboard and diagonal stripes are always available.
   */
  function getAvailableLayouts(colorCount: number): LayoutMode[] {
    const layouts: LayoutMode[] = ['horizontal', 'vertical'];
    if (colorCount === 4 || colorCount === 6) {
      layouts.push('grid');
    }
    if (colorCount !== 4) {
      layouts.push('star');
    }
    layouts.push('checkerboard', 'diagonalStripes');
    return layouts;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950">
      {/* Tea Time experience - only on own profile, triggers after 1 minute of idle */}
      <TeaTimeOverlay enabled={isOwnProfile} idleTimeout={60000} moments={moments} username={metadata?.name} />

      {/* Confetti celebration */}
      <Confetti 
        colors={confettiColors} 
        active={showConfetti} 
        onComplete={() => setShowConfetti(false)} 
      />

      {/* Curtain transition between palette and emoji picker */}
      <CurtainTransition
        isActive={showCurtainTransition}
        onMidpoint={handleCurtainMidpointProfile}
        onComplete={handleCurtainCompleteProfile}
        colors={pendingColors}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background border-b border-border safe-top-mobile">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-4">
          <Link
            ref={backButtonRef}
            to="/"
            onClick={(e) => {
              e.preventDefault();
              handleBack();
            }}
            className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-6 w-6 text-foreground" />
          </Link>
          <span className="text-lg font-medium flex-1 text-foreground">
            {isOwnProfile ? 'my profile' : 'profile'}
          </span>
          {isOwnProfile && (
            <Link
              to="/settings/profile"
              className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
              title="Edit profile"
            >
              <Settings className="h-5 w-5 text-foreground" />
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 pb-12 safe-bottom">
        {/* Profile header */}
        <div className="py-8 flex flex-col items-center text-center space-y-4">
          {/* Avatar */}
          {safeImageUrl(picture) ? (
            <img
              src={safeImageUrl(picture)}
              alt={displayName}
              className="w-24 h-24 rounded-full object-cover ring-4 ring-white/50"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-400 via-amber-300 to-sky-400 flex items-center justify-center text-3xl">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          
          <div>
            <h1 className="text-2xl font-semibold">{displayName}</h1>
            {about && (
              <p className="text-muted-foreground mt-2 max-w-sm">{about}</p>
            )}
          </div>
        </div>

        {/* Color fingerprint constellation */}
        {moments && moments.length >= 2 && (
          <div className="mb-10">
            <PaletteConstellation moments={moments} />
          </div>
        )}

        {/* User's moments */}
        <div className="space-y-10">
          <h2 className="text-lg font-medium px-2">
            {isOwnProfile ? 'Your moments' : 'Their moments'}
          </h2>
          
          {isLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-square rounded-3xl overflow-hidden">
                    <Skeleton className="w-full h-full" />
                  </div>
                </div>
              ))}
            </>
          ) : moments && moments.length > 0 ? (
            moments.map((moment) => (
              <ColorMomentCard 
                key={moment.event.id} 
                moment={moment} 
                onRemix={user ? handleRemix : undefined}
              />
            ))
          ) : (
            <div className="py-12 text-center">
              <p className="text-muted-foreground">No moments yet</p>
            </div>
          )}
        </div>
      </main>

      {/* Capture Modal with Wavy Transition */}
      {showColorPicker && user && (
        isTransitioning ? (
          <WavyTransition
            isActive={isTransitioning}
            onTransitionComplete={handleTransitionComplete}
            buttonRect={buttonRect}
          >
            <CaptureModeSelector
              onComplete={handleColorsSelected}
              onCancel={handleCancelCapture}
              initialColors={selectedColors.length > 0 ? selectedColors : undefined}
            />
          </WavyTransition>
        ) : (
          <CaptureModeSelector
            onComplete={handleColorsSelected}
            onCancel={handleCancelCapture}
            initialColors={selectedColors.length > 0 ? selectedColors : undefined}
          />
        )
      )}

      {/* Emoji Modal */}
      {showEmojiPicker && user && (
        <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 flex flex-col overflow-y-auto">
          {/* Header */}
          <div className="flex items-center gap-4 p-4">
            <button
              onClick={() => {
                setShowEmojiPicker(false);
                setShowColorPicker(true);
                setIsTransitioning(false);
              }}
              className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col items-center px-6 pt-2 pb-32">
            <div className="w-full max-w-sm space-y-6">
              {/* Name field */}
              <input
                type="text"
                value={selectedName}
                onChange={(e) => setSelectedName(e.target.value)}
                placeholder="Name this moment..."
                className="w-full px-4 py-3 rounded-2xl bg-white/60 dark:bg-white/10 border-0 text-center text-lg placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-foreground/20"
                maxLength={50}
              />

              {/* Preview with layout */}
              <ColorPaletteDisplay
                colors={selectedColors}
                layout={selectedLayout}
                className="aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-black/10"
              >
                {selectedEmoji && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <span className="text-6xl drop-shadow-lg">{selectedEmoji}</span>
                  </div>
                )}

                {/* Layout toggle button */}
                {(() => {
                  const availableLayouts = getAvailableLayouts(selectedColors.length);
                  const LayoutIcon = LAYOUT_ICONS[selectedLayout];
                  return (
                    <button
                      onClick={() => {
                        const currentIndex = availableLayouts.indexOf(selectedLayout);
                        const nextIndex = (currentIndex + 1) % availableLayouts.length;
                        setSelectedLayout(availableLayouts[nextIndex]);
                      }}
                      className="absolute bottom-3 left-3 p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-all z-20"
                      title="Change layout"
                    >
                      <LayoutIcon className="h-4 w-4" />
                    </button>
                  );
                })()}
              </ColorPaletteDisplay>

              <p className="text-center text-xl text-muted-foreground">
                How does it feel?
              </p>

              <EmojiPicker onSelect={handleEmojiSelected} maxHeight={220} />

              {selectedEmoji && (
                <button
                  onClick={handleClearEmoji}
                  className="w-full py-4 text-muted-foreground hover:text-foreground transition-colors text-lg"
                >
                  just the colors
                </button>
              )}
            </div>
          </div>

          {/* Ready button */}
          <button
            onClick={handleSubmit}
            className="fixed bottom-10 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto z-50 px-6 py-4 rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white font-medium text-lg shadow-xl shadow-amber-300/40 dark:shadow-amber-900/40 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center gap-2"
          >
            {selectedEmoji && <span className="text-2xl">{selectedEmoji}</span>}
            <span>Ready?</span>
          </button>
        </div>
      )}
    </div>
  );
}

function EventView({ eventId, authorHint }: { eventId: string; authorHint?: string }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedLayout, setSelectedLayout] = useState<LayoutMode>('horizontal');
  const [selectedName, setSelectedName] = useState('');
  const [selectedEmoji, setSelectedEmoji] = useState('');
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiColors, setConfettiColors] = useState<string[]>([]);
  const [showCurtainTransition, setShowCurtainTransition] = useState(false);
  const [pendingColors, setPendingColors] = useState<string[]>([]);
  const { mutate: createEvent } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch the event
  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filter: any = { ids: [eventId], kinds: [3367], limit: 1 };
      if (authorHint) {
        filter.authors = [authorHint];
      }
      const events = await nostr.query([filter]);
      return events[0];
    },
  });

  const author = useAuthor(event?.pubkey || '');
  const displayName = author.data?.metadata?.name || genUserName(event?.pubkey || '');

  const handleRemix = (colors: string[], rect: DOMRect) => {
    setSelectedColors(colors);
    setButtonRect(rect);
    setIsTransitioning(true);
    setShowColorPicker(true);
  };

  const handleTransitionComplete = useCallback(() => {
    // Transition complete
  }, []);

  const handleCancelCapture = () => {
    setIsTransitioning(false);
    setShowColorPicker(false);
  };

  const handleColorsSelected = (colors: string[]) => {
    // Store colors and trigger curtain transition
    setPendingColors(colors);
    setShowCurtainTransition(true);
  };

  const handleCurtainMidpointEvent = () => {
    // At midpoint (curtains fully closed), switch screens
    setSelectedColors(pendingColors);
    setIsTransitioning(false);
    setShowColorPicker(false);
    setShowEmojiPicker(true);
  };

  const handleCurtainCompleteEvent = () => {
    setShowCurtainTransition(false);
    setPendingColors([]);
  };

  const handleEmojiSelected = (emoji: string) => {
    setSelectedEmoji(emoji);
  };

  const handleClearEmoji = () => {
    setSelectedEmoji('');
  };

  const handleSubmit = () => {
    if (!user) return;

    const colors = [...selectedColors];
    const layout = selectedLayout;
    const name = selectedName.trim();
    const emoji = selectedEmoji;
    
    setShowEmojiPicker(false);
    setConfettiColors(colors.length > 0 ? colors : ['#f43f5e', '#f59e0b', '#10b981', '#3b82f6']);
    setShowConfetti(true);
    setSelectedColors([]);
    setSelectedLayout('horizontal');
    setSelectedName('');
    setSelectedEmoji('');

    const tags: string[][] = [
      ...colors.map(color => ['c', color]),
      ['layout', layout],
      ['alt', `Color moment: ${colors.join(', ')}`],
    ];
    if (name) {
      tags.push(['name', name]);
    }

    createEvent({
      kind: 3367,
      content: emoji,
      tags,
    }, {
      onSuccess: () => {
        if (emoji) {
          const toastMessage = getEmojiToast(emoji, 'vibe');
          toast({ title: `${emoji} ${toastMessage}` });
        } else {
          toast({ title: '✨ Shared!' });
        }
        queryClient.invalidateQueries({ queryKey: ['color-moments-infinite'] });
        queryClient.invalidateQueries({ queryKey: ['following-moments-infinite'] });
      },
    });
  };

  function getAvailableLayouts(colorCount: number): LayoutMode[] {
    const layouts: LayoutMode[] = ['horizontal', 'vertical'];
    if (colorCount === 4 || colorCount === 6) {
      layouts.push('grid');
    }
    if (colorCount !== 4) {
      layouts.push('star');
    }
    layouts.push('checkerboard', 'diagonalStripes');
    return layouts;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950">
      <header className="sticky top-0 z-40 bg-background border-b border-border safe-top-mobile">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-4">
            <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10">
              <ArrowLeft className="h-6 w-6 text-foreground" />
            </Link>
          </div>
        </header>
        <main className="max-w-xl mx-auto px-5 py-12">
          <Skeleton className="aspect-square rounded-3xl" />
        </main>
      </div>
    );
  }

  // Transform event to ColorMoment using shared logic
  const moment = event ? transformToColorMoment(event) : null;

  if (!event || !moment) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950">
      <Confetti 
        colors={confettiColors} 
        active={showConfetti} 
        onComplete={() => setShowConfetti(false)} 
      />

      {/* Curtain transition between palette and emoji picker */}
      <CurtainTransition
        isActive={showCurtainTransition}
        onMidpoint={handleCurtainMidpointEvent}
        onComplete={handleCurtainCompleteEvent}
        colors={pendingColors}
      />

      <header className="sticky top-0 z-40 bg-background border-b border-border safe-top-mobile">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-4">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10">
            <ArrowLeft className="h-6 w-6 text-foreground" />
          </Link>
          <span className="text-lg font-medium flex-1 text-foreground">
            {displayName}'s moment
          </span>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-5 py-12 safe-bottom">
        <ColorMomentCard moment={moment} onRemix={user ? handleRemix : undefined} />
      </main>

      {/* Capture Modal with Wavy Transition */}
      {showColorPicker && user && (
        isTransitioning ? (
          <WavyTransition
            isActive={isTransitioning}
            onTransitionComplete={handleTransitionComplete}
            buttonRect={buttonRect}
          >
            <CaptureModeSelector
              onComplete={handleColorsSelected}
              onCancel={handleCancelCapture}
              initialColors={selectedColors.length > 0 ? selectedColors : undefined}
            />
          </WavyTransition>
        ) : (
          <CaptureModeSelector
            onComplete={handleColorsSelected}
            onCancel={handleCancelCapture}
            initialColors={selectedColors.length > 0 ? selectedColors : undefined}
          />
        )
      )}

      {/* Emoji Modal - same as ProfileView */}
      {showEmojiPicker && user && (
        <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 flex flex-col overflow-y-auto">
          <div className="flex items-center gap-4 p-4">
            <button
              onClick={() => {
                setShowEmojiPicker(false);
                setShowColorPicker(true);
                setIsTransitioning(false);
              }}
              className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center px-6 pt-2 pb-32">
            <div className="w-full max-w-sm space-y-6">
              <input
                type="text"
                value={selectedName}
                onChange={(e) => setSelectedName(e.target.value)}
                placeholder="Name this moment..."
                className="w-full px-4 py-3 rounded-2xl bg-white/60 dark:bg-white/10 border-0 text-center text-lg placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-foreground/20"
                maxLength={50}
              />

              <ColorPaletteDisplay
                colors={selectedColors}
                layout={selectedLayout}
                className="aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-black/10"
              >
                {selectedEmoji && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <span className="text-6xl drop-shadow-lg">{selectedEmoji}</span>
                  </div>
                )}

                {(() => {
                  const LayoutIcon = LAYOUT_ICONS[selectedLayout];
                  return (
                    <button
                      onClick={() => {
                        const availableLayouts = getAvailableLayouts(selectedColors.length);
                        const currentIndex = availableLayouts.indexOf(selectedLayout);
                        const nextIndex = (currentIndex + 1) % availableLayouts.length;
                        setSelectedLayout(availableLayouts[nextIndex]);
                      }}
                      className="absolute bottom-3 left-3 p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-all z-20"
                      title="Change layout"
                    >
                      <LayoutIcon className="h-4 w-4" />
                    </button>
                  );
                })()}
              </ColorPaletteDisplay>

              <p className="text-center text-xl text-muted-foreground">
                How does it feel?
              </p>

              <EmojiPicker onSelect={handleEmojiSelected} maxHeight={220} />

              {selectedEmoji && (
                <button
                  onClick={handleClearEmoji}
                  className="w-full py-4 text-muted-foreground hover:text-foreground transition-colors text-lg"
                >
                  just the colors
                </button>
              )}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            className="fixed bottom-10 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto z-50 px-6 py-4 rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white font-medium text-lg shadow-xl shadow-amber-300/40 dark:shadow-amber-900/40 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center gap-2"
          >
            {selectedEmoji && <span className="text-2xl">{selectedEmoji}</span>}
            <span>Ready?</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) {
    return <NotFound />;
  }

  let decoded;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  const { type, data } = decoded;

  switch (type) {
    case 'npub':
      return <ProfileView pubkey={data as string} />;

    case 'nprofile': {
      const profileData = data as { pubkey: string; relays?: string[] };
      return <ProfileView pubkey={profileData.pubkey} />;
    }

    case 'note':
      return <EventView eventId={data as string} />;

    case 'nevent': {
      const eventData = data as { id: string; author?: string; relays?: string[] };
      return <EventView eventId={eventData.id} authorHint={eventData.author} />;
    }

    case 'naddr':
      // Addressable events not used in this app
      return <NotFound />;

    default:
      return <NotFound />;
  }
}
